import React, { useState, useEffect } from "react";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import MenuIcon from "@mui/icons-material/Menu";
import Drawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import ListItemIcon from "@mui/material/ListItemIcon";
import { Link, useLocation } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { motion } from "framer-motion";

// Icons
import HomeIcon from "@mui/icons-material/Home";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import ExploreIcon from "@mui/icons-material/Explore";
import InfoIcon from "@mui/icons-material/Info";
import ContactPhoneIcon from "@mui/icons-material/ContactPhone";
import FlightTakeoffIcon from "@mui/icons-material/FlightTakeoff";

const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Check if current route matches the nav item
  const isActive = (path) => {
    if (path === "/" && location.pathname === "/") return true;
    if (path !== "/" && location.pathname.startsWith(path)) return true;
    return false;
  };

  // Handle scroll effect for header
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleDrawerOpen = () => {
    setOpen(true);
  };

  const handleDrawerClose = () => {
    setOpen(false);
  };

  // Navigation items with icons
  const navItems = [
    { text: "HOME", path: "/", icon: <HomeIcon /> },
    { text: "SERVICES", path: "/services", icon: <DirectionsCarIcon /> },
    { text: "TOUR PACKS", path: "/tour-packs", icon: <ExploreIcon /> },
    { text: "ABOUT US", path: "/about-us", icon: <InfoIcon /> },
    { text: "CONTACT US", path: "/contact-us", icon: <ContactPhoneIcon /> },
  ];

  // Animation variants
  const navVariants = {
    hidden: { opacity: 0, y: -20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { 
        duration: 0.5, 
        staggerChildren: 0.1,
        ease: "easeOut"
      }
    }
  };

  const navItemVariants = {
    hidden: { opacity: 0, y: -10 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.3 }
    }
  };

  return (
    <motion.div
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ type: "spring", stiffness: 100, damping: 20 }}
    >
      <AppBar
        position="fixed"
        sx={{
          backgroundColor: scrolled ? "rgba(219, 53, 42, 0.95)" : "#db352a",
          minHeight: { xs: 56, sm: 70 },
          boxShadow: scrolled 
            ? "0 4px 20px rgba(0, 0, 0, 0.15)" 
            : "none",
          transition: "all 0.3s ease",
        }}
      >
        <Toolbar
          sx={{
            justifyContent: "space-between",
            width: "100%",
            maxWidth: "1200px",
            mx: "auto",
            px: { xs: 2, sm: 4 },
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <IconButton
              aria-label="open drawer"
              sx={{
                display: { xs: "flex", sm: "none" },
                color: "white",
                mr: 1,
              }}
              onClick={handleDrawerOpen}
            >
              <MenuIcon />
            </IconButton>

            <motion.div
              whileHover={{ scale: 1.03 }}
              transition={{ type: "spring", stiffness: 400, damping: 10 }}
            >
              <Box 
                sx={{ 
                  display: "flex", 
                  alignItems: "center",
                  cursor: "pointer",
                  textDecoration: "none"
                }}
                component={Link}
                to="/"
              >
                <FlightTakeoffIcon sx={{ 
                  color: "white", 
                  mr: 1.5, 
                  fontSize: { xs: "1.8rem", md: "2rem" },
                  transform: "rotate(-15deg)"
                }} />
                                  <Typography
                  variant="h6"
                  component="div"
                  sx={{
                    fontWeight: "bold",
                    fontSize: { xs: "1.2rem", sm: "1.5rem" },
                    whiteSpace: "nowrap",
                    letterSpacing: "0.5px",
                    color: "white"
                  }}
                >
                  Ashoka Tours
                </Typography>
              </Box>
            </motion.div>
          </Box>

          <motion.div
            variants={navVariants}
            initial="hidden"
            animate="visible"
          >
            <Box
              sx={{
                display: { xs: "none", sm: "flex" },
                gap: { sm: 0.5, md: 2 },
              }}
            >
              {navItems.map((item) => (
                <motion.div key={item.text} variants={navItemVariants}>
                  <Button
                    component={Link}
                    to={item.path}
                    sx={{
                      color: "white",
                      mx: { sm: 0.5, md: 1 },
                      px: { sm: 1.5, md: 2 },
                      position: "relative",
                      fontWeight: isActive(item.path) ? "bold" : "normal",
                      "&::after": isActive(item.path) ? {
                        content: '""',
                        position: "absolute",
                        width: "70%",
                        height: "3px",
                        backgroundColor: "white",
                        bottom: "5px",
                        left: "15%",
                        borderRadius: "2px",
                      } : {},
                      "&:hover": {
                        backgroundColor: "rgba(255, 255, 255, 0.1)",
                      },
                      transition: "all 0.2s ease"
                    }}
                  >
                    {item.text}
                  </Button>
                </motion.div>
              ))}
            </Box>
          </motion.div>
        </Toolbar>

        <Drawer
          variant="temporary"
          open={open}
          onClose={handleDrawerClose}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: "block", sm: "none" },
            "& .MuiDrawer-paper": { 
              width: 250,
              background: "linear-gradient(135deg, #fff 0%, #f9f9f9 100%)",
            },
          }}
        >
          <Box sx={{ 
            display: "flex",
            flexDirection: "column",
            height: "100%"
          }}>
            <Box sx={{ 
              p: 2, 
              display: "flex", 
              alignItems: "center",
              borderBottom: "1px solid rgba(0,0,0,0.1)",
              backgroundColor: "#db352a"
            }}>
              <FlightTakeoffIcon sx={{ color: "white", mr: 1 }} />
              <Typography 
                variant="h6" 
                sx={{ 
                  fontWeight: "bold",
                  color: "white"
                }}
              >
                Ashoka Tours
              </Typography>
            </Box>
            
            <List sx={{ pt: 2, flex: 1 }}>
              {navItems.map((item) => (
                <ListItem
                  button
                  key={item.text}
                  component={Link}
                  to={item.path}
                  onClick={handleDrawerClose}
                  sx={{
                    mb: 1,
                    mx: 1,
                    borderRadius: "8px",
                    backgroundColor: isActive(item.path) 
                      ? "rgba(219, 53, 42, 0.1)" 
                      : "transparent",
                    "&:hover": {
                      backgroundColor: "rgba(219, 53, 42, 0.05)",
                    }
                  }}
                >
                  <ListItemIcon sx={{ 
                    color: "#db352a",
                    minWidth: "40px"
                  }}>
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText 
                    primary={item.text} 
                    primaryTypographyProps={{
                      fontWeight: isActive(item.path) ? "bold" : "normal",
                      color: "#db352a"
                    }}
                  />
                </ListItem>
              ))}
            </List>
            
            <Box sx={{ 
              p: 2, 
              borderTop: "1px solid rgba(0,0,0,0.1)",
              textAlign: "center",
              fontSize: "0.8rem",
              color: "rgba(0,0,0,0.6)"
            }}>
              © 2025 Ashoka Tours and Travels
            </Box>
          </Box>
        </Drawer>
      </AppBar>
      
      {/* Spacer for fixed header */}
      <Toolbar />
    </motion.div>
  );
};

export default Header;